import { formatoFechaSinHora } from "../utilities/formatoFecha";
import { idEstadosOrden } from "./ordenHelper";

const orderStepMap = {
    pendiente: {
        label: "Pendiente",
        iconClass: "fa fa-shopping-cart",
        meta: "Orden registrada"
    },
    pagoAprobado: {
        label: "Pago aprobado",
        iconClass: "fa fa-credit-card",
        meta: "Circuito habilitado"
    },
    facturado: {
        label: "Facturado",
        iconClass: "fa fa-file-alt",
        meta: "Comprobante cargado"
    },
    preparacion: {
        label: "En preparacion",
        iconClass: "fa fa-box-open",
        meta: "Pedido en armado"
    },
    enviado: {
        label: "Enviado",
        iconClass: "fa fa-truck",
        meta: "Despacho en camino"
    },
    entregado: {
        label: "Entregado",
        iconClass: "fa fa-map-marker",
        meta: "Operacion finalizada"
    },
    solicitudAnulacion: {
        label: "Solicitud de anulacion",
        iconClass: "fa fa-undo",
        meta: "Pendiente de respuesta"
    },
    anulacionRechazada: {
        label: "Anulacion rechazada",
        iconClass: "fa fa-times-circle",
        meta: "La orden vuelve al circuito"
    },
    anulado: {
        label: "Anulado",
        iconClass: "fa fa-ban",
        meta: "Venta cancelada",
        tone: "danger"
    }
};

const orderBaseFlow = [
    "pendiente",
    "pagoAprobado",
    "facturado",
    "preparacion",
    "enviado",
    "entregado"
];

const publicationMap = {
    pendiente_activacion: {
        label: "Pendiente",
        iconClass: "fa fa-hourglass-half",
        meta: "Estado inicial"
    },
    activo: {
        label: "Activa",
        iconClass: "fa fa-check-circle",
        meta: "Visible en marketplace"
    },
    en_revision: {
        label: "En revision",
        iconClass: "fa fa-search",
        meta: "Validacion interna"
    },
    pausado: {
        label: "Pausada",
        iconClass: "fa fa-pause-circle",
        meta: "Publicacion detenida"
    },
    reactivado: {
        label: "Activa",
        iconClass: "fa fa-play-circle",
        meta: "Vuelve a publicarse"
    },
    eliminado: {
        label: "Eliminada",
        iconClass: "fa fa-trash",
        meta: "Fuera de catalogo",
        tone: "danger"
    }
};

const liquidacionDefinitions = [
    {
        key: "preparacion",
        label: "Preparacion",
        iconClass: "fa fa-clipboard-list",
        meta: "Ordenes y novedades"
    },
    {
        key: "calculo",
        label: "Calculo fiscal",
        iconClass: "fa fa-calculator",
        meta: "Retenciones y percepciones"
    },
    {
        key: "confirmacion",
        label: "Confirmacion",
        iconClass: "fa fa-check-square",
        meta: "Revision contable"
    },
    {
        key: "facturacion",
        label: "Facturacion",
        iconClass: "fa fa-file-alt",
        meta: "Comprobante emitido"
    }
];

const formatTimelineDate = (value) => {
    if (!value) {
        return "";
    }

    if (typeof value !== "string") {
        return "";
    }

    return value.includes("T") ? formatoFechaSinHora(value) : value;
};

const getCurrentOrCompletedState = (isCurrent, isCompleted) => {
    if (isCurrent) {
        return "current";
    }

    if (isCompleted) {
        return "completed";
    }

    return "upcoming";
};

const buildOrderStep = (stepKey, state, date = "", metaOverride) => ({
    key: `orden-${stepKey}`,
    label: orderStepMap[stepKey].label,
    iconClass: orderStepMap[stepKey].iconClass,
    meta: metaOverride || orderStepMap[stepKey].meta,
    date,
    state,
    tone: state === "upcoming" ? undefined : orderStepMap[stepKey].tone
});

const getOrderTrackingUrl = (orden = {}) => {
    const domicilioTracking = orden?.entrega?.entregaDomicilio?.urlTracking;
    const sucursalTracking = orden?.entrega?.entregaSucursalCorreo?.urlTracking;

    return [domicilioTracking, sucursalTracking].find(
        (value) => typeof value === "string" && value.trim() !== ""
    ) || "";
};

const getAchievedBaseOrderSteps = ({ paymentApproved, hasFacturado, hasTracking, hasEntregado }) => {
    const achieved = ["pendiente"];

    if (paymentApproved || hasFacturado || hasTracking || hasEntregado) {
        achieved.push("pagoAprobado");
    }

    if (hasFacturado || hasTracking || hasEntregado) {
        achieved.push("facturado", "preparacion");
    }

    if (hasTracking || hasEntregado) {
        achieved.push("enviado");
    }

    if (hasEntregado) {
        achieved.push("entregado");
    }

    return achieved;
};

const getCurrentBaseOrderStep = ({ paymentApproved, hasFacturado, hasTracking, hasEntregado }) => {
    if (hasEntregado) {
        return "entregado";
    }

    if (hasTracking) {
        return "enviado";
    }

    if (hasFacturado) {
        return "preparacion";
    }

    if (paymentApproved) {
        return "pagoAprobado";
    }

    return "pendiente";
};

const getOrderStepDate = (stepKey, historyById = {}, currentEstadoId) => {
    switch (stepKey) {
        case "pendiente":
            return formatTimelineDate(historyById[idEstadosOrden.pendiente]?.fecha);
        case "facturado":
            return formatTimelineDate(historyById[idEstadosOrden.facturado]?.fecha);
        case "entregado":
            return formatTimelineDate(historyById[idEstadosOrden.entregado]?.fecha);
        case "solicitudAnulacion":
            return formatTimelineDate(historyById[idEstadosOrden.solicitarAnulacion]?.fecha);
        case "anulacionRechazada":
            return currentEstadoId === idEstadosOrden.pendiente
                ? formatTimelineDate(historyById[idEstadosOrden.pendiente]?.fecha)
                : "";
        case "anulado":
            return formatTimelineDate(historyById[idEstadosOrden.anulado]?.fecha);
        default:
            return "";
    }
};

export const buildOrderTimeline = (orden = {}, estadoVentaAprobado = null) => {
    const estados = Array.isArray(orden?.estado) ? orden.estado : [];

    if (estados.length === 0) {
        return [];
    }

    const historyById = estados.reduce((acc, estado) => {
        if (estado?.idEstado != null && !acc[estado.idEstado]) {
            acc[estado.idEstado] = estado;
        }

        return acc;
    }, {});

    const currentEstadoId = estados[0]?.idEstado;
    const hasFacturadoHistory = Boolean(historyById[idEstadosOrden.facturado]);
    const hasSolicitudAnulacion = Boolean(historyById[idEstadosOrden.solicitarAnulacion]);
    const hasEntregadoHistory = Boolean(historyById[idEstadosOrden.entregado]) || currentEstadoId === idEstadosOrden.entregado;
    const hasAnulado = Boolean(historyById[idEstadosOrden.anulado]) ||
        Boolean(orden?.anulacionMegatone?.anulado) ||
        currentEstadoId === idEstadosOrden.anulado;
    const motivoAnulacion = orden?.motivoAnulacion?.detalle || orden?.anulacionMegatone?.motivoAnulacion || "";
    const hasMotivoAnulacion = Boolean(
        motivoAnulacion ||
        Number(orden?.motivoAnulacion?.idMotivo ?? orden?.motivoAnulacion?.id ?? 0)
    );
    const isSolicitudRechazada =
        currentEstadoId === idEstadosOrden.pendiente &&
        hasSolicitudAnulacion &&
        !hasAnulado &&
        hasMotivoAnulacion;
    const shouldClampPendingProgress = currentEstadoId === idEstadosOrden.pendiente && !isSolicitudRechazada;
    const hasFacturado = shouldClampPendingProgress ? false : hasFacturadoHistory;
    const hasEntregado = shouldClampPendingProgress ? false : hasEntregadoHistory;
    const hasTracking = shouldClampPendingProgress
        ? false
        : (Boolean(getOrderTrackingUrl(orden)) || hasEntregado);
    const paymentApproved = shouldClampPendingProgress
        ? (typeof estadoVentaAprobado === "boolean" ? estadoVentaAprobado : false)
        : (typeof estadoVentaAprobado === "boolean"
            ? (estadoVentaAprobado || hasFacturado || hasTracking || hasEntregado)
            : (hasFacturado || hasTracking || hasEntregado));
    const currentBaseStep = getCurrentBaseOrderStep({
        paymentApproved,
        hasFacturado,
        hasTracking,
        hasEntregado
    });
    const achievedBaseSteps = getAchievedBaseOrderSteps({
        paymentApproved,
        hasFacturado,
        hasTracking,
        hasEntregado
    });

    if (hasAnulado) {
        const completedBaseSteps = achievedBaseSteps.filter((stepKey) => stepKey !== "entregado");

        return [
            ...completedBaseSteps.map((stepKey) =>
                buildOrderStep(
                    stepKey,
                    "completed",
                    getOrderStepDate(stepKey, historyById, currentEstadoId)
                )
            ),
            ...(hasSolicitudAnulacion ? [
                buildOrderStep(
                    "solicitudAnulacion",
                    "completed",
                    getOrderStepDate("solicitudAnulacion", historyById, currentEstadoId),
                    motivoAnulacion || undefined
                )
            ] : []),
            buildOrderStep(
                "anulado",
                "current",
                getOrderStepDate("anulado", historyById, currentEstadoId),
                motivoAnulacion || undefined
            )
        ];
    }

    if (currentEstadoId === idEstadosOrden.solicitarAnulacion) {
        return [
            ...achievedBaseSteps.map((stepKey) =>
                buildOrderStep(
                    stepKey,
                    "completed",
                    getOrderStepDate(stepKey, historyById, currentEstadoId)
                )
            ),
            buildOrderStep(
                "solicitudAnulacion",
                "current",
                getOrderStepDate("solicitudAnulacion", historyById, currentEstadoId),
                motivoAnulacion || undefined
            ),
            buildOrderStep("anulado", "upcoming")
        ];
    }

    if (isSolicitudRechazada) {
        const completedBaseSteps = achievedBaseSteps.filter((stepKey) => stepKey !== "enviado" && stepKey !== "entregado");

        return [
            ...completedBaseSteps.map((stepKey) =>
                buildOrderStep(
                    stepKey,
                    "completed",
                    getOrderStepDate(stepKey, historyById, currentEstadoId)
                )
            ),
            buildOrderStep(
                "solicitudAnulacion",
                "completed",
                getOrderStepDate("solicitudAnulacion", historyById, currentEstadoId)
            ),
            buildOrderStep(
                "anulacionRechazada",
                "current",
                getOrderStepDate("anulacionRechazada", historyById, currentEstadoId),
                motivoAnulacion || undefined
            )
        ];
    }

    const currentStepIndex = orderBaseFlow.indexOf(currentBaseStep);

    return orderBaseFlow.map((stepKey, index) =>
        buildOrderStep(
            stepKey,
            getCurrentOrCompletedState(index === currentStepIndex, index < currentStepIndex),
            getOrderStepDate(stepKey, historyById, currentEstadoId)
        )
    );
};

export const buildPublicationTimeline = (publicacion = {}, motivosRevision = []) => {
    const currentState = publicacion?.estado;

    if (!currentState) {
        return [];
    }

    const currentDate = formatTimelineDate(publicacion?.fechaUltimaModificacion);
    const motivoRevision = Array.isArray(motivosRevision)
        ? motivosRevision.find((motivo) => `${motivo.codigo}` === `${publicacion?.idMotivoRevision}`)?.descripcion
        : "";
    const buildPublicationStep = (stepKey, state, date = "", metaOverride) => ({
        key: `publicacion-${stepKey}`,
        label: publicationMap[stepKey].label,
        iconClass: publicationMap[stepKey].iconClass,
        meta: metaOverride || publicationMap[stepKey].meta,
        date,
        state,
        tone: publicationMap[stepKey].tone
    });

    if (currentState === "eliminado") {
        return [
            buildPublicationStep("pendiente_activacion", "completed"),
            buildPublicationStep("activo", "completed"),
            buildPublicationStep("eliminado", "current", currentDate)
        ];
    }

    if (currentState === "pendiente_activacion") {
        return [
            buildPublicationStep("pendiente_activacion", "current", currentDate),
            buildPublicationStep("activo", "upcoming", "", "Siguiente paso")
        ];
    }

    if (currentState === "activo") {
        return [
            buildPublicationStep("pendiente_activacion", "completed"),
            buildPublicationStep("activo", "current", currentDate)
        ];
    }

    if (currentState === "en_revision") {
        return [
            buildPublicationStep("pendiente_activacion", "completed"),
            buildPublicationStep("activo", "completed", "", "Primera activacion"),
            buildPublicationStep(
                "en_revision",
                "current",
                currentDate,
                motivoRevision || publicacion?.observacionesRevision || publicationMap.en_revision.meta
            ),
            buildPublicationStep("reactivado", "upcoming")
        ];
    }

    if (currentState === "pausado") {
        return [
            buildPublicationStep("pendiente_activacion", "completed"),
            buildPublicationStep("activo", "completed", "", "Primera activacion"),
            buildPublicationStep("pausado", "current", currentDate),
            buildPublicationStep("reactivado", "upcoming")
        ];
    }

    return [
        buildPublicationStep("pendiente_activacion", "completed"),
        buildPublicationStep("activo", "current", currentDate)
    ];
};

export const buildLiquidacionTimeline = (liquidacion = {}) => {
    if (!liquidacion || Object.keys(liquidacion).length === 0) {
        return [];
    }

    const isConfirmada = liquidacion?.idTipoEstadoLiquidacion === 3;
    const hasInvoiceData = Boolean(
        liquidacion?.numeroComprobante || liquidacion?.descripcionTipoFactura || liquidacion?.codigoSap
    );
    const hasFiscalData = isConfirmada || Number(liquidacion?.totalRetenciones || 0) > 0 || Number(liquidacion?.totalPercepcion || 0) > 0;
    const currentDate = formatTimelineDate(liquidacion?.mostrarFecha);
    const invoiceMeta = [liquidacion?.descripcionTipoFactura, liquidacion?.numeroComprobante]
        .filter(Boolean)
        .join(" - ");

    return liquidacionDefinitions.map((definition) => {
        let state = "upcoming";

        if (definition.key === "preparacion") {
            state = !hasFiscalData && !isConfirmada && !hasInvoiceData ? "current" : "completed";
        }

        if (definition.key === "calculo") {
            state = hasFiscalData && !isConfirmada ? "current" : hasFiscalData && isConfirmada ? "completed" : "upcoming";
        }

        if (definition.key === "confirmacion") {
            state = isConfirmada && !hasInvoiceData ? "current" : hasInvoiceData ? "completed" : "upcoming";
        }

        if (definition.key === "facturacion") {
            state = hasInvoiceData ? "current" : "upcoming";
        }

        return {
            key: `liquidacion-${definition.key}`,
            label: definition.label,
            iconClass: definition.iconClass,
            meta: definition.key === "facturacion" && invoiceMeta ? invoiceMeta : definition.meta,
            date: state === "current" ? currentDate : "",
            state
        };
    });
};
